// Role → UI capability derivation (LLD 11 v0.2).
//
// Capabilities are computed from the live session's
// `role_snapshots[currentRole].permissions` set. This replaces the prior
// hardcoded `{ FLEET_ADMIN: { canDownload: true, ... } }` map; the keys
// on the returned record are kept stable so the ~25 call-sites that
// already use `caps.canDownload` etc. don't need to change.
//
// Reserved permissions (`role:manage`, `rule:manage`, `tenant:manage`,
// `user:force_logout`) only ever resolve true for the `tenant_admin`
// system role; custom roles cannot hold them.
//
// `scopeDescription` is derived from the session's `effective_rules` for
// the active role: it reports the count of rules and the unique location
// ids they cover. For `tenant_admin` (or any role with an `ALL × ALL`
// rule) we report "Full tenant access".

import type { EffectiveRule, RoleSnapshot, SessionMe } from "@/session";

/** Permission keys recognised by the v0.2 catalog. */
const PERM = {
  CAMERA_VIEW: "camera:view",
  CAMERA_VIEW_LIVE: "camera:view_live",
  RECORDING_VIEW: "recording:view",
  RECORDING_EXPORT: "recording:export",
  AUDIT_VIEW: "audit:view",
  AUDIT_EXPORT: "audit:export",
  LOCATION_VIEW: "location:view",
  LOCATION_ADMIN: "location:admin",
  ROLE_MANAGE: "role:manage",
  RULE_MANAGE: "rule:manage",
  TENANT_MANAGE: "tenant:manage",
  USER_FORCE_LOGOUT: "user:force_logout",
  USER_MANAGE: "user:manage",
  GROUP_MANAGE: "group:manage",
} as const;

export interface RoleCapabilities {
  /** Whether the admin surface (rules/groups/users/grants) is visible.
   *  Derived: any of the reserved-tier permissions OR audit/location-admin. */
  canSeeAdmin: boolean;
  /** Can download a recording to disk. */
  canDownload: boolean;
  /** Can view multiple cameras simultaneously (grid + side-by-side).
   *  Derived from `camera:view_live` — the multi-cam wall is a live-view
   *  feature, so we gate it on the same key. */
  canViewMultiCamera: boolean;
  /** Can trigger force-logout on another user (reserved → tenant_admin). */
  canForceLogout: boolean;

  // --- Cameras / live view ------------------------------------------------
  canViewLive: boolean;

  // --- Locations ----------------------------------------------------------
  canViewLocations: boolean;
  canAdminLocations: boolean;

  // --- Audit --------------------------------------------------------------
  canViewAudit: boolean;
  canExportAudit: boolean;

  // --- Reserved (tenant_admin only) --------------------------------------
  canManageRoles: boolean;
  canManageRules: boolean;
  canManageTenant: boolean;
  /** Tenant admin can CRUD user identities (schema-equivalent to SCIM). */
  canManageUsers: boolean;
  /** Tenant admin can CRUD groups + memberships (schema-equivalent to SCIM). */
  canManageGroups: boolean;

  /** Human-readable label for the active role. */
  roleLabel: string;
  /**
   * One-line explanation of what the current role is allowed to do at
   * what scope. Used by the role-aware banner on the Cameras page.
   */
  scopeDescription: string;
}

/** Fallback "no role / no session" capability set — everything off. */
const NONE: RoleCapabilities = {
  canSeeAdmin: false,
  canDownload: false,
  canViewMultiCamera: false,
  canForceLogout: false,
  canViewLive: false,
  canViewLocations: false,
  canAdminLocations: false,
  canViewAudit: false,
  canExportAudit: false,
  canManageRoles: false,
  canManageRules: false,
  canManageTenant: false,
  canManageUsers: false,
  canManageGroups: false,
  roleLabel: "None",
  scopeDescription: "No role selected.",
};

function permsFor(
  session: SessionMe | null,
  role: string | null,
): { perms: Set<string>; snapshot: RoleSnapshot | null } {
  if (session === null || role === null || role === "") {
    return { perms: new Set(), snapshot: null };
  }
  const snapshots = session.role_snapshots ?? {};
  // Try direct lookup first, then case-insensitive — the BFF emits
  // exact-case role ids but legacy storage may have UPPERCASE.
  const direct = snapshots[role];
  if (direct) {
    return { perms: new Set(direct.permissions ?? []), snapshot: direct };
  }
  const lower = role.toLowerCase();
  for (const [key, snap] of Object.entries(snapshots)) {
    if (key.toLowerCase() === lower) {
      return { perms: new Set(snap.permissions ?? []), snapshot: snap };
    }
  }
  return { perms: new Set(), snapshot: null };
}

/** Pretty role label — prefer the snapshot's display_name, fall back to a
 *  friendly form of the role_id. */
function labelFor(role: string | null, snapshot: RoleSnapshot | null): string {
  if (snapshot?.display_name) return snapshot.display_name;
  if (role === null || role === "") return "None";
  if (role.toLowerCase() === "tenant_admin") return "Tenant admin";
  // Title-case the slug: site_viewer → "Site viewer".
  return role
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Filter the session's effective rules to those for the active role.
 *  Tolerant of missing `effective_rules` on older BFF builds. */
function rulesForRole(
  session: SessionMe | null,
  role: string | null,
): EffectiveRule[] {
  if (session === null || role === null) return [];
  const all = session.effective_rules ?? [];
  const lower = role.toLowerCase();
  return all.filter(
    (r) => r.role_id === role || r.role_id.toLowerCase() === lower,
  );
}

/** Summarise the active role's effective rules into a one-line scope label. */
function describeScope(
  role: string | null,
  rules: EffectiveRule[],
  perms: Set<string>,
): string {
  if (role === null || role === "") return "No role selected.";
  // tenant_admin (or any role with an ALL × ALL rule) gets the canonical
  // "full tenant access" line — keeps the previous wording for consumers.
  const isTenantAdmin = role.toLowerCase() === "tenant_admin";
  const hasAllRule = rules.some(
    (r) => r.location_scope.mode === "ALL" && r.device_scope.mode === "ALL",
  );
  if (isTenantAdmin || hasAllRule) {
    return "Full tenant access. Every location, every camera.";
  }
  if (rules.length === 0) {
    // The role is held in `distinct_roles` but no rule routed it. Either
    // a snapshot/rule mismatch or a transient state during reconcile.
    if (perms.size > 0) {
      return "Role has no active rules — access is empty until a rule is added.";
    }
    return "No active rules for this role.";
  }
  // Count unique locations across rules. ALLOW lists contribute their ids;
  // DENY/ALL contribute "all-tenant" — we just count the rules in that case.
  const locIds = new Set<string>();
  let hasAllLocations = false;
  for (const r of rules) {
    if (r.location_scope.mode === "ALL" || r.location_scope.mode === "DENY") {
      hasAllLocations = true;
    }
    if (r.location_scope.mode === "ALLOW") {
      for (const id of r.location_scope.ids) locIds.add(id);
    }
  }
  const ruleWord = rules.length === 1 ? "rule" : "rules";
  if (hasAllLocations) {
    return `Access via ${rules.length} ${ruleWord}, spanning all locations.`;
  }
  const locWord = locIds.size === 1 ? "location" : "locations";
  return `Access to ${locIds.size} ${locWord} via ${rules.length} ${ruleWord}.`;
}

/** Compute the capability bag for `(session, role)`. Pure function. */
export function computeCapabilities(
  session: SessionMe | null,
  currentRole: string | null,
): RoleCapabilities {
  if (currentRole === null || currentRole === "") {
    return { ...NONE };
  }
  const { perms, snapshot } = permsFor(session, currentRole);
  if (perms.size === 0 && snapshot === null) {
    // Edge case for old BFFs that don't yet emit role_snapshots: fall back
    // to a "tenant_admin gets everything" assumption keyed on the role id
    // alone. Keeps the legacy admin surface lit while the backend ships.
    if (currentRole.toLowerCase() === "tenant_admin") {
      return tenantAdminFallback();
    }
    return {
      ...NONE,
      roleLabel: labelFor(currentRole, null),
      scopeDescription:
        "Backend has not yet shipped per-role permission snapshots.",
    };
  }
  const has = (k: string): boolean => perms.has(k);
  const rules = rulesForRole(session, currentRole);

  const canManageRoles = has(PERM.ROLE_MANAGE);
  const canManageRules = has(PERM.RULE_MANAGE);
  const canManageTenant = has(PERM.TENANT_MANAGE);
  const canForceLogout = has(PERM.USER_FORCE_LOGOUT);
  const canManageUsers = has(PERM.USER_MANAGE);
  const canManageGroups = has(PERM.GROUP_MANAGE);

  return {
    canSeeAdmin:
      canManageRoles ||
      canManageRules ||
      canManageTenant ||
      canForceLogout ||
      canManageUsers ||
      canManageGroups ||
      has(PERM.LOCATION_ADMIN) ||
      has(PERM.AUDIT_VIEW),
    canDownload: has(PERM.RECORDING_EXPORT),
    canViewMultiCamera: has(PERM.CAMERA_VIEW_LIVE),
    canForceLogout,

    canViewLive: has(PERM.CAMERA_VIEW_LIVE),

    canViewLocations: has(PERM.LOCATION_VIEW),
    canAdminLocations: has(PERM.LOCATION_ADMIN),

    canViewAudit: has(PERM.AUDIT_VIEW),
    canExportAudit: has(PERM.AUDIT_EXPORT),

    canManageRoles,
    canManageRules,
    canManageTenant,
    canManageUsers,
    canManageGroups,

    roleLabel: labelFor(currentRole, snapshot),
    scopeDescription: describeScope(currentRole, rules, perms),
  };
}

function tenantAdminFallback(): RoleCapabilities {
  return {
    canSeeAdmin: true,
    canDownload: true,
    canViewMultiCamera: true,
    canForceLogout: true,
    canViewLive: true,
    canViewLocations: true,
    canAdminLocations: true,
    canViewAudit: true,
    canExportAudit: true,
    canManageRoles: true,
    canManageRules: true,
    canManageTenant: true,
    canManageUsers: true,
    canManageGroups: true,
    roleLabel: "Tenant admin",
    scopeDescription: "Full tenant access. Every location, every camera.",
  };
}

// ---------------------------------------------------------------------------
// Back-compat shim: `capabilitiesFor(role)` still exists so the existing
// callers (`RequireAdmin`, `AdminLayout`, `Cameras`, `Recordings`,
// `CameraDetail`) keep compiling. Internally it pulls the latest session
// out of a thread-local "current session" set by the SessionProvider.
//
// We don't have access to the session in pure-function callers, so the
// shim falls back to a hardcoded role-id heuristic (`tenant_admin` →
// everything; anything else → minimal) when no session has been latched.
// Pages that need the real values should switch to `useCapabilities()`.
// ---------------------------------------------------------------------------

let latchedSession: SessionMe | null = null;

/** Set by SessionProvider whenever the session changes. Pages should use
 *  the `useCapabilities()` hook instead of this back-channel. */
export function _setLatchedSession(session: SessionMe | null): void {
  latchedSession = session;
}

/**
 * @deprecated Prefer `useCapabilities()` which reads session + role from
 * React context. This shim exists only so the pre-LLD-11 call sites keep
 * working until they're migrated.
 */
export function capabilitiesFor(role: string | null): RoleCapabilities {
  return computeCapabilities(latchedSession, role);
}

/**
 * Whether the active role-grant pair should grant fleet-wide visibility.
 * Useful for components that want to render "all locations" copy without
 * shipping a hardcoded location list.
 *
 * Under LLD 11 v0.2: a session has fleet-wide visibility when any
 * effective rule for the active role has `location_scope.mode === "ALL"`
 * (or `DENY`, since deny lists are still tenant-wide allow on misses).
 * The function keeps the prior signature for back-compat; the `grants`
 * argument is unused under v0.2 but tolerated for migration.
 */
export function hasFleetWideGrant(
  role: string | null,
  _grants: ReadonlyArray<unknown>,
): boolean {
  if (role === null) return false;
  if (role.toLowerCase() === "tenant_admin") return true;
  const rules = rulesForRole(latchedSession, role);
  return rules.some(
    (r) => r.location_scope.mode === "ALL" || r.location_scope.mode === "DENY",
  );
}
